import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import type { InvoiceDto, InvoiceOptionsDto, InvoicePrintsDto, PosStateDto, PrintRecordDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { claimBrowserPrint, confirmPrinted, pendingFor, printDirect, retryFailed, roleMapping, settlePending, type AgentPaper, type PrintView } from "../../print/direct";
import { logoUrl, receiptDoc } from "../../print/documents";
import { carriesQr, Phase1Qr } from "../../print/qr";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import type { IconName } from "../../components/icons";
import { Wordmark } from "../../components/Shell";
import { loadPrintImage } from "../../print/render";
import { claimWindow, linkApp, openErp, POS_WINDOW } from "../../lib/windows";
import { amt, instant } from "../ledger/common";
import { Barcode } from "../items/common";
import "../../styles/pos.css";
import { partyName } from "../../lib/party";
import { DemoPrintMark } from "../../components/Demo";

// The production POS register (phase 11a; rules W-1 to W-6, provisional), ported from the approved prototype
// (assets/pos.js and pos.css): its own full-screen frame with the Home, Sale, Refund, Invoices and Reports tabs.
// Every sale and refund here is completed by the server at once (posted later by the business owner, W-15). When
// Delta cannot be reached, Delta POS for Windows opens the offline register instead (OfflineRegister.tsx;
// docs/offline-pos.md), prepared from the card on Home.

const TABS: [string, string, IconName][] = [["/pos", "posTabHome", "home"], ["/pos/sale", "posTabSale", "cart"], ["/pos/refund", "posTabRefund", "refund"], ["/pos/invoices", "posTabInvoices", "receipt"], ["/pos/reports", "posTabReports", "chart"]];
const THEME = "delta.posTheme";

export function PosShell({ active, children }: { active: string; children: ReactNode }) {
  const { t, lang, setLang } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const [theme, setTheme] = useState(() => { try { return localStorage.getItem(THEME) === "dark" ? "dark" : "light"; } catch { return "light"; } });
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);
  useEffect(() => { document.title = `${t("posTitle")} · Delta`; }, [t]);
  // This window is the register's: the ERP's POS links bring it to the front and tell it where to go.
  useEffect(() => claimWindow(POS_WINDOW, (p) => nav(p)), [nav]);
  useEffect(() => linkApp("pos"), []);
  const flip = () => setTheme((x) => { const n = x === "dark" ? "light" : "dark"; try { localStorage.setItem(THEME, n); } catch { /* storage blocked */ } return n; });
  const me = s.me!;
  const initials = lang === "ar" ? me.user.name.slice(0, 1) : me.user.name.split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="pos" data-theme={theme}>
      <header className="pos-top">
        {/* In the register the logo stays in the register: POS Home, with the cart kept for the shift (D-5). */}
        <div className="pos-brand"><Wordmark light to="/pos" label={t("posHome")} /><span className="pos-badge">{t("posTitle")}</span></div>
        <nav className="pos-tabs" aria-label={t("posTitle")}>
          {TABS.filter(([r]) => !s.missingFor(r).length).map(([r, k, ic]) => (
            <Link key={r} to={r} className={active === r ? "active" : ""} aria-current={active === r ? "page" : undefined}><Icon name={ic} /><span>{t(k)}</span></Link>
          ))}
        </nav>
        <div className="pos-tools">
          <span className="demo-badge pos-env" title={t("environmentTag")}><Icon name="flask" /><span>{t("environmentTag")}</span></span>
          <button className="pos-icon-btn" onClick={flip} aria-label={theme === "dark" ? t("lightMode") : t("darkMode")} title={theme === "dark" ? t("lightMode") : t("darkMode")}><Icon name={theme === "dark" ? "sun" : "moon"} /></button>
          <button className="pos-icon-btn pos-lang" onClick={() => setLang(lang === "ar" ? "en" : "ar")} lang={lang === "ar" ? "en" : "ar"}><Icon name="globe" /><span>{t("switchLang")}</span></button>
          <div style={{ position: "relative" }} ref={ref}>
            <button className="pos-user" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)} data-testid="pos-user"><span className="avatar">{initials}</span><span className="pu-name">{me.user.name}</span></button>
            <div className={`menu-pop ${menu ? "open" : ""}`} role="menu">
              <a role="menuitem" href="/home" onClick={(e) => { e.preventDefault(); setMenu(false); openErp("/home"); }} data-testid="pos-to-erp"><Icon name="home" />{t("backToDashboard")}</a>
              {!s.missingFor("/pos/printers").length && <Link role="menuitem" to="/pos/printers" data-testid="pos-printers-link"><Icon name="printer" />{t("printersThisPc")}</Link>}
              <button role="menuitem" onClick={async () => { setMenu(false); await s.signOut(); nav("/login"); }}><Icon name="logout" className="flip" />{t("signOut")}</button>
            </div>
          </div>
        </div>
      </header>
      <main className="pos-main">{children}</main>
    </div>
  );
}

export const Amt = ({ v }: { v: string | null | undefined }) => {
  const { t } = useLang();
  return <span className="amt"><span className="num">{amt(v)}</span> <span className="cur">{t("currency")}</span></span>;
};

/** The register's state (the cashier's open shift, the POS customer, the seller) and, when asked, the sale options. */
export function usePos(withOptions: boolean) {
  const { t } = useLang();
  const [state, setState] = useState<PosStateDto | null>(null);
  const [opts, setOpts] = useState<InvoiceOptionsDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const load = useCallback(async () => {
    setFailed(null);
    try {
      const [s, o] = await Promise.all([api<PosStateDto>("GET", "/pos/state"), withOptions ? api<InvoiceOptionsDto>("GET", "/invoices/options") : Promise.resolve(null)]);
      setState(s);
      if (o) setOpts(o);
    } catch (e) { setFailed(errorText(t, e)); }
  }, [withOptions, t]);
  useEffect(() => { void load(); }, [load]);
  return { state, opts, failed, reload: load };
}

export function PosLoading({ failed, onRetry }: { failed: string | null; onRetry(): void }) {
  const { t } = useLang();
  return <div className="state-box">{failed ? <><p role="alert">{failed}</p><button className="btn" onClick={onRetry}>{t("retry")}</button></> : <p>{t("loading")}</p>}</div>;
}

/** No open shift: the sale and refund tabs send the cashier to Home to open one (W-2). */
export function NoShift() {
  const { t } = useLang();
  return (
    <div className="pos-page"><div className="state-box" data-testid="pos-no-shift"><Icon name="lock" /><h2>{t("noShiftTitle")}</h2><p>{t("noShiftBody")}</p>
      <Link className="btn btn-primary" to="/pos">{t("openShift")}</Link></div></div>
  );
}

/** A time stamp from the API as "2026-10-26 09:05" in Saudi Arabia time (UTC+3 all year). */
export const riyadhTime = instant;

/** A random request id (W-4), made once per sale or refund and sent again on every retry. */
export const newRequestId = () => crypto.randomUUID();

export type PrintMode = "thermal" | "a4";

/**
 * The receipt of a POS sale or refund as the browser shows and prints it (the fallback when no agent printer is
 * set). Non-fiscal (docs/non-fiscal-pos.md): it says so, has no tax-invoice title, and names the compliant system
 * that issues the legal tax invoice. It carries the phase 1 QR (print/qr.tsx; not phase 2), or a box saying the
 * seller's tax details are missing or invalid. A server that is not production marks it TEST DOCUMENT. The agent
 * prints the same content (print/documents.ts).
 */
export function ReceiptBody({ inv, seller, mode, buyerTaxNumber, copyNo = 0, legalReference, testDocument }: {
  inv: InvoiceDto; seller: PosStateDto["seller"]; mode: PrintMode; buyerTaxNumber?: string; copyNo?: number; legalReference?: string | null; testDocument?: boolean;
}) {
  const { t, L, lang } = useLang();
  const refund = inv.kind === "sale_refund";
  const sellerName = lang === "ar" ? seller.nameAr || seller.nameEn : seller.nameEn || seller.nameAr;
  const pays: [string, string][] = inv.payments.map((p) => [L(p.treasury), p.amount]);
  if (!refund && !/^0(\.0+)?$/.test(inv.remaining) && !inv.remaining.startsWith("-")) pays.push([t("deferred"), inv.remaining]);
  return (
    <div className={`receipt ${mode}`} data-testid="receipt" data-copy={copyNo} dir={lang === "ar" ? "rtl" : "ltr"}>
      {testDocument && <div className="rc-test" data-testid="receipt-test">{t("printTestBanner")}</div>}
      <DemoPrintMark className="rc-test rc-demo" />
      {copyNo > 0 && <div className="rc-banner" data-testid="receipt-copy">{t("printCopyBanner", { n: copyNo })}</div>}
      <div className="rc-banner" data-testid="receipt-nonfiscal">{refund ? t("nonFiscalRefundTitle") : t("nonFiscalTitle")}</div>
      <div className="rc-head">{logoUrl(seller) && <img className="rc-logo" src={logoUrl(seller)!} alt="" data-testid="receipt-logo" />}<div className="rc-co">{sellerName}</div>
        {seller.address && <div className="rc-meta">{seller.address}</div>}
        <div className="rc-meta">{L(inv.branch)} · {L(inv.warehouse)}</div>
        <div className="rc-meta">{t("vatNo")}: <span className="num" data-testid="receipt-vat-no">{seller.taxNumber || "–"}</span></div></div>
      <div className="rc-kv"><span>{t("receiptNo")}</span><b className="num" data-testid="receipt-code">{inv.code}</b></div>
      {refund && inv.refundOf && <div className="rc-kv"><span>{t("refundOfLabel")}</span><b className="num">{inv.refundOf.code}</b></div>}
      <div className="rc-kv"><span>{t("date")}</span><b className="num">{inv.invoiceAt.replace("T", " ")}</b></div>
      <div className="rc-kv"><span>{t("customerName")}</span><b>{partyName(t, inv.party)}</b></div>
      {buyerTaxNumber && <div className="rc-kv"><span>{t("vatNo")}</span><b className="num">{buyerTaxNumber}</b></div>}
      <div className="rc-kv"><span>{t("cashier")}</span><b>{inv.completedByName ?? inv.postedByName ?? inv.createdByName}</b></div>
      {inv.shift && <div className="rc-kv"><span>{t("shiftNo")}</span><b className="num">{inv.shift.number}</b></div>}
      <table className="rc-lines"><thead><tr><th>{t("items")}</th><th className="end">{t("qtyShort")}</th><th className="end">{t("price")}</th><th className="end">{t("total")}</th></tr></thead>
        <tbody>{inv.lines.map((l) => <tr key={l.lineNo}><td>{L(l.item)}<small>{L(l.unit)}</small></td><td className="end num">{amtQty(l.qty)}</td><td className="end num">{amt(l.price)}</td><td className="end num">{amt(l.total)}</td></tr>)}</tbody></table>
      <div className="rc-tot">
        {!refund && <><div><span>{t("totalBeforeDiscount")}</span><span className="num">{amt(inv.totalBefore)}</span></div><div><span>{t("discount")}</span><span className="num">{/[1-9]/.test(inv.discount) ? "-" : ""}{amt(inv.discount)}</span></div></>}
        <div><span>{t("subTotal")}</span><span className="num">{amt(inv.subtotal)}</span></div>
        {/[1-9]/.test(inv.tobaccoFee ?? "0") && <div data-testid="receipt-tobacco"><span>{t("tobFee")}</span><span className="num">{amt(inv.tobaccoFee)}</span></div>}
        <div><span>{t("vatIncluded")}</span><span className="num">{amt(inv.vat)}</span></div>
        <div className="rc-grand"><span>{refund ? t("refundTotal") : t("grandTotal")}</span><span><span className="num" data-testid="receipt-grand">{amt(inv.grand)}</span> {t("currency")}</span></div>
        {!refund && /[1-9]/.test(inv.tobacco?.raised ?? "0") && <p className="rc-note" data-testid="receipt-tobacco-raised">{t("tobRaisedNote", { x: amt(inv.tobacco!.raised) })}</p>}
        {pays.map(([n, a], i) => <div key={i}><span>{n}</span><span className="num">{amt(a)}</span></div>)}
        {inv.cashTendered && <><div><span>{t("cashReceived")}</span><span className="num" data-testid="receipt-tendered">{amt(inv.cashTendered)}</span></div>
          <div><span>{t("changeDue")}</span><span className="num" data-testid="receipt-change">{amt(inv.cashChange ?? "0.00")}</span></div></>}
      </div>
      <div className="rc-notice" data-testid="receipt-notice">{seller.legalInvoiceSystem ? t("nonFiscalNoticeSystem", { system: seller.legalInvoiceSystem }) : t("nonFiscalNotice")}</div>
      {legalReference && <div className="rc-kv"><span>{t("legalInvoiceNo")}</span><b className="num" data-testid="receipt-legal-ref">{legalReference}</b></div>}
      {carriesQr(inv) && <Phase1Qr seller={seller} inv={inv} t={t} mm={mode === "a4" ? 30 : 28} className="rc-qrbox" />}
      {inv.code && <div className="rc-codes"><Barcode text={inv.code} h={36} /><span className="num rc-bcno">{inv.code}</span></div>}
      <div className="rc-thanks">{t("thankYou")}</div>
    </div>
  );
}

const amtQty = (q: string) => q.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");

const AUTO_KEY = "delta.print.auto";
/** Whether this workstation prints a sale's receipt by itself once the sale is posted (on unless turned off here). */
export const autoPrintOn = () => { try { return localStorage.getItem(AUTO_KEY) !== "off"; } catch { return true; } };
export const setAutoPrint = (on: boolean) => { try { localStorage.setItem(AUTO_KEY, on ? "on" : "off"); } catch { /* storage blocked */ } };

/**
 * Sends a sale's or refund's receipt to this PC's mapped printer through the POS agent: the original once (a copy when
 * `copy`), never through the browser's print dialog. Used by the receipt dialog and by one-click Cash/mada, which
 * prints with no dialog at all. Printing never completes, posts or changes the sale.
 */
export async function printReceiptDirect(o: {
  inv: InvoiceDto; seller: PosStateDto["seller"]; testDocument: boolean; buyerTaxNumber?: string; legalReference: string | null; mode: PrintMode; copy: boolean;
  fresh?: boolean; user: string; paper: AgentPaper | null | undefined; i18n: Parameters<typeof receiptDoc>[2];
}, onView: (v: PrintView) => void): Promise<PrintView> {
  const { inv, mode, copy, paper } = o;
  const cash = inv.payments.some((x) => (x.treasury as { kind?: string }).kind === "cash");
  // The logo is printed when it loads; without one (or offline) the organization's name heads the receipt alone.
  const url = logoUrl(o.seller), logo = url ? await loadPrintImage(url) : null;
  return printDirect({
    invoiceId: inv.id, role: mode === "a4" ? "a4" : "receipt", layout: mode === "a4" ? "a4" : paper?.kind === "58mm" ? "receipt_58" : "receipt_80", copy,
    title: `${inv.code ?? ""}${copy ? " copy" : ""}`, user: o.user, openDrawer: !!o.fresh && !copy && inv.kind !== "sale_refund" && cash,
    build: (copyNo, pp) => receiptDoc(inv, o.seller, o.i18n, { copyNo, buyerTaxNumber: o.buyerTaxNumber, legalReference: o.legalReference, large: pp.kind === "a4", testDocument: o.testDocument, logo })
  }, onView);
}

/** What a print view says (exported for the register's print banner). */
export const printViewText = (t: (k: string, v?: Record<string, string | number>) => string, v: PrintView) => printText(t, v);
export const printReasonText = (t: (k: string, v?: Record<string, string | number>) => string, reason: string) =>
  (t(`printReason_${reason}`) !== `printReason_${reason}` ? t(`printReason_${reason}`) : reason);

const VIEW_TEXT: Record<string, string> = {
  claiming: "printWorking", rendering: "printWorking", sending: "printWorking", queued: "printQueued", spooled: "printSpooled"
};

/** What a print view says, and the reason code as a sentence. */
function printText(t: (k: string, v?: Record<string, string | number>) => string, v: PrintView): string {
  if (v.kind === "working") return t(VIEW_TEXT[v.state] ?? "printWorking");
  if (v.kind === "done") return t("printDone", { printer: v.job.printer ?? "" });
  if (v.kind === "original_exists") return t("printOriginalExists", { ws: v.record.workstation });
  const reason = t(`printReason_${v.reason}`) !== `printReason_${v.reason}` ? t(`printReason_${v.reason}`) : v.reason;
  return v.kind === "unknown" ? t("printUnknown", { reason }) : t("printFailed", { reason });
}

/**
 * The receipt dialog: preview, 80 mm or A4, direct printing on this PC's printer through the POS agent (the original
 * once, copies marked COPY), the browser's print dialog as the fallback, and the sale's legal invoice number.
 */
export function ReceiptModal({ inv, seller, testDocument, fresh, buyerTaxNumber, onClose, extra, autoPrint }: {
  inv: InvoiceDto; seller: PosStateDto["seller"]; testDocument: boolean; fresh?: boolean; buyerTaxNumber?: string; onClose(): void; extra?: ReactNode; autoPrint?: boolean;
}) {
  const { t, L, lang } = useLang();
  const s = useSession();
  const toast = useToast();
  const [mode, setMode] = useState<PrintMode>("thermal");
  const [prints, setPrints] = useState<InvoicePrintsDto | null>(null);
  const [view, setView] = useState<PrintView | null>(null);
  const [direct, setDirect] = useState<{ thermal: AgentPaper | null; a4: AgentPaper | null; reason: string | null } | null>(null);
  const [htmlCopy, setHtmlCopy] = useState(0);
  const [ref, setRef] = useState("");
  const [refErr, setRefErr] = useState<string | null>(null);
  const started = useRef(false);
  const refund = inv.kind === "sale_refund";
  const loadPrints = useCallback(async () => { try { setPrints(await api<InvoicePrintsDto>("GET", `/invoices/${inv.id}/prints`)); } catch { /* shown as unknown */ } }, [inv.id]);
  useEffect(() => {
    document.body.classList.add("rc-open");
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => { document.body.classList.remove("rc-open"); document.removeEventListener("keydown", k); };
  }, [onClose]);
  useEffect(() => {
    void (async () => {
      await settlePending();
      const [r, a] = await Promise.all([roleMapping("receipt"), roleMapping("a4")]);
      setDirect({ thermal: r.ok ? r.mapping.paper : null, a4: a.ok ? a.mapping.paper : null, reason: r.ok ? null : r.reason });
      await loadPrints();
    })();
  }, [loadPrints]);
  const original = prints?.prints.find((p) => p.copyNo === 0) ?? null;
  const legal = prints?.externalRefs.find((r) => !r.voidedAt)?.reference ?? null;
  const paper = mode === "a4" ? direct?.a4 : direct?.thermal;
  const busy = view?.kind === "working";

  const printNow = useCallback(async (copy: boolean) => {
    await printReceiptDirect({ inv, seller, testDocument, buyerTaxNumber, legalReference: legal, mode, copy, fresh, user: s.me!.user.name, paper: mode === "a4" ? direct?.a4 : direct?.thermal, i18n: { t, L, lang } }, setView);
    await loadPrints();
  }, [mode, direct, inv, s.me, fresh, seller, testDocument, t, L, lang, buyerTaxNumber, legal, loadPrints]);

  // A fresh sale prints its receipt by itself when this PC has a receipt printer and automatic printing is on.
  useEffect(() => {
    if (!autoPrint || !fresh || started.current || !direct?.thermal || !prints || prints.prints.length || pendingFor(inv.id).length || !autoPrintOn()) return;
    started.current = true;
    void printNow(false);
  }, [autoPrint, fresh, direct, prints, inv.id, printNow]);

  const browserPrint = async () => {
    try {
      const r = await claimBrowserPrint(inv.id, mode === "a4" ? "a4" : "receipt_80", !!original);
      const rec = "originalExists" in r ? (await claimBrowserPrint(inv.id, mode === "a4" ? "a4" : "receipt_80", true)) as PrintRecordDto : r;
      setHtmlCopy(rec.copyNo);
      await loadPrints();
      setTimeout(() => window.print(), 50);
    } catch (e) { toast(errorText(t, e)); }
  };
  const confirm = async () => {
    if (view && (view.kind === "unknown" || view.kind === "working") && view.record && view.job) {
      await confirmPrinted(view.record, view.job.jobId, s.me!.user.name);
      setView(null);
      await loadPrints();
      toast(t("printConfirmed"));
    }
  };
  const saveRef = async () => {
    setRefErr(null);
    try { setPrints(await api<InvoicePrintsDto>("POST", `/invoices/${inv.id}/legal-reference`, { reference: ref })); setRef(""); toast(t("legalInvoiceSaved")); }
    catch (e) { setRefErr(errorText(t, e)); }
  };
  const legalRef = prints?.externalRefs.find((r) => !r.voidedAt) ?? null;
  const [voidNote, setVoidNote] = useState<string | null>(null);
  const voidRef = async () => {
    if (!legalRef || !voidNote?.trim()) return;
    setRefErr(null);
    try { setPrints(await api<InvoicePrintsDto>("POST", `/invoices/${inv.id}/legal-reference/${legalRef.id}/void`, { note: voidNote.trim() })); setVoidNote(null); toast(t("legalInvoiceVoided")); }
    catch (e) { setRefErr(errorText(t, e)); }
  };
  const canRecord = s.can(refund ? "invoiceSaleRefundAdd" : "invoiceSaleAdd") && !legal && inv.status !== "draft";
  const canVoid = s.can(refund ? "invoiceSaleRefundUpdate" : "invoiceSaleUpdate");

  return createPortal(
    <div className="modal-back open rc-back" role="dialog" aria-modal="true" aria-label={t("receiptPreview")} data-testid="receipt-modal">
      <style>{`@page { size: ${mode === "a4" ? "A4" : "80mm auto"}; margin: ${mode === "a4" ? "12mm" : "0"}; }`}</style>
      <div className="modal rc-modal">
        <div className="modal-head"><h3>{fresh ? <><Icon name="check" /> {refund ? t("refundPosted", { no: inv.code ?? "" }) : `${t("settled")} · ${inv.code}`}</> : t("receiptPreview")}</h3>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label={t("close")}><Icon name="x" /></button></div>
        <div className="rc-toolbar"><div className="segmented">{(["thermal", "a4"] as const).map((v) => (
          <label key={v}><input type="radio" name="rc-mode" value={v} checked={mode === v} onChange={() => setMode(v)} /><span>{v === "a4" ? t("a4Print") : t("thermalPrint")}</span></label>))}</div></div>
        <div className="rc-print-state" data-testid="print-status" data-state={view ? (view.kind === "working" ? view.state : view.kind) : original ? `original_${original.outcome}` : "none"} role="status">
          {view ? printText(t, view)
            : original ? t("printOriginalState", { ws: original.workstation, state: t(`printOutcome_${original.outcome}`), copies: (prints?.prints.length ?? 1) - 1 })
            : direct === null ? t("loading") : paper ? t("printReady") : t(`printReason_${direct.reason ?? "no_printer"}`)}
          {view?.kind === "unknown" && <div className="rc-print-help">{t("printUnknownHelp")}</div>}
        </div>
        <div className="rc-stage"><ReceiptBody inv={inv} seller={seller} mode={mode} buyerTaxNumber={buyerTaxNumber} copyNo={htmlCopy} legalReference={legal} testDocument={testDocument} /></div>
        {(canRecord || legal) && <div className="rc-legal">
          {legal ? <><span data-testid="legal-ref">{t("legalInvoiceNo")}: <b className="num">{legal}</b></span>
              {canVoid && (voidNote === null ? <button className="btn btn-sm btn-ghost" onClick={() => setVoidNote("")} data-testid="legal-ref-void">{t("legalInvoiceVoid")}</button>
                : <><input className="control" value={voidNote} maxLength={500} onChange={(e) => setVoidNote(e.target.value)} placeholder={t("legalInvoiceVoidNote")} aria-label={t("legalInvoiceVoidNote")} data-testid="legal-ref-void-note" />
                  <button className="btn btn-sm" onClick={() => void voidRef()} disabled={!voidNote.trim()} data-testid="legal-ref-void-save">{t("save")}</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setVoidNote(null)}>{t("cancel")}</button></>)}
              {refErr && <span className="field-error" role="alert">{refErr}</span>}</>
            : <><label htmlFor="rc-legal-ref">{prints?.legalInvoiceSystem ? t("legalInvoiceRecordIn", { system: prints.legalInvoiceSystem }) : t("legalInvoiceRecord")}</label>
              <input id="rc-legal-ref" className="control" value={ref} maxLength={60} onChange={(e) => setRef(e.target.value)} data-testid="legal-ref-input" dir="ltr" />
              <button className="btn btn-sm" onClick={() => void saveRef()} disabled={!ref.trim()} data-testid="legal-ref-save">{t("save")}</button>
              {refErr && <span className="field-error" role="alert">{refErr}</span>}</>}
        </div>}
        <div className="modal-foot">
          {extra}
          {view?.kind === "unknown" && view.job && <button className="btn" onClick={() => void confirm()} data-testid="print-confirm"><Icon name="check" />{t("printConfirm")}</button>}
          {view?.kind === "failed" && view.job && view.record && <button className="btn" onClick={() => void retryFailed(view.job!, view.record!, setView).then(loadPrints)} data-testid="print-retry"><Icon name="refresh" />{t("retry")}</button>}
          {paper && (original || view?.kind === "original_exists" ? <button className="btn" disabled={busy} onClick={() => void printNow(true)} data-testid="print-copy"><Icon name="printer" />{t("printCopy")}</button>
            : <button className="btn" disabled={busy} onClick={() => void printNow(false)} data-testid="print-direct"><Icon name="printer" />{t("print")}</button>)}
          <button className="btn" onClick={() => void browserPrint()} data-testid="receipt-print"><Icon name="printer" />{paper ? t("printBrowser") : t("print")}</button>
          <button className="btn btn-primary" onClick={onClose} data-testid="receipt-close">{fresh && !refund ? <><Icon name="plus" />{t("newSale")}</> : t("close")}</button>
        </div>
      </div>
    </div>, document.body);
}
